import { graphql } from '$houdini'
import type { Parameter } from '@isoftdata/svelte-dashboard'
import type { AppContext } from 'types/common'
import { chartToUnionType, formatReportOutput } from 'utility/dashboard-helper'
import component from './Dashboard.svelte'
import showErrorAndRedirect from 'utility/show-error-and-redirect'

export default function ({ stateRouter, mediator, checkSessionPermission, i18next: { t: translate } }: AppContext) {
	const defaultParameters: Array<Parameter> = [
		{ name: 'date_range', type: 'dateRange', title: translate('common:dateRange', 'Date Range'), default: 'This Year' },
		{ name: 'site', type: 'siteSelection', title: translate('common:store', 'Store'), default: '0' },
	]

	stateRouter.addState({
		name: 'app.configuration.dashboard',
		route: 'dashboard',
		defaultChild: 'report',
		template: {
			svelte: true,
			component,
		},
		async resolve() {
			const canEditOwn = checkSessionPermission('DASHBOARD_REPORT_EDIT_OWN')
			const canEditAll = checkSessionPermission('DASHBOARD_REPORT_EDIT_ALL')

			if (!canEditOwn && !canEditAll) {
				console.error("You don't have permission to edit dashboard reports.")
				throw showErrorAndRedirect(
					mediator,
					translate('error.permissionDenied', 'Permission Denied'),
					translate(
						'error.youDoNotHavePermissionToEditDashboardReports',
						'You do not have permission to edit dashboard reports.',
					),
					{
						name: 'app.configuration',
						params: {},
					},
				)
			}

			const { data } = await configDataQuery.fetch({
				variables: {
					args: {
						editableByCurrentUser: true,
					},
				},
			})

			if (!data) {
				throw showErrorAndRedirect(
					mediator,
					translate('error.errorLoadingDashboardConfiguration', 'Error Loading Dashboard Configuration'),
					translate('common:noDataReturnErrorMessage', 'No data was returned from the server'),
					{
						name: 'app.configuration',
						params: {},
					},
				)
			}

			return {
				defaultParameters,
				reports: data.dashboardReports.map(formatReportOutput),
				groups: data.groups,
				users: data.users,
				stores: data.stores,
				canEditOwn,
				canEditAll,
				charts: data.charts.filter(chart => chart.showChart).map(chartToUnionType),
			}
		},
	})
}

const configDataQuery = graphql(`
	query DashboardConfigurationData($args: DashboardReportArgs) {
		dashboardReports(args: $args) {
			dashboardReportId: id
			reportName: name
			reportTitle: title
			autoRefreshInterval
			json
			shareType
			shareId
			ownerId
		}
		charts: dashboardCharts {
			chartId: id
			name
			title
			supertype
			showChart: display
			# description
			# dateSourceName
			json: jsonDefault
		}
		groups {
			id
			name
		}
		users: userAccounts {
			id
			name
			firstName
			lastName
		}
		stores {
			id
			code
			name
		}
	}
`)
